import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
} from '@angular/core';
import { Page, Component as BrComponent } from '@bloomreach/spa-sdk';
import { SalesChargeBreakPoint, Yields } from '@models';
import { BaseProductDetailComponent } from '@products/base-product-detail.component';
import { DistributionHistory } from '@products/models/distribution-history';
import { EMDASH } from '@products/utils/constants/product.constants';
import { hasValue } from '@products/utils/mappers/mapper.utils';
import { SiteConfigService } from '@services';
import {
  ConfigurationId,
  DistributionLatestConfig,
  DistributionSchedules,
  DistributionState,
  DistributionTableConfig,
  LatestDistribution,
  ProductDetailConfiguration,
} from '@types';
import { Logger } from '@utils/logger';
import cloneDeep from 'lodash/cloneDeep';
import { Subscription } from 'rxjs';
import { PricingDistributionDisplayComponent } from '../pricing-distribution-component.interface';
import { PricingDistributionConfig } from '../pricing-distribution.config';
import { DistributionService } from '../services/distribution.service';

const logger = Logger.getLogger('DistributionComponent');

@Component({
  selector: 'ft-distribution',
  templateUrl: './distribution.component.html',
  styleUrls: ['./distribution.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DistributionComponent
  extends BaseProductDetailComponent
  implements PricingDistributionDisplayComponent, OnDestroy {
  componentConfiguration: PricingDistributionConfig;
  dataSubscription: Subscription[] = [];
  salesChargeBreakPoints: SalesChargeBreakPoint[];
  latestConfig: DistributionLatestConfig;
  tableConfig: DistributionTableConfig;
  filteredHistoricalList: DistributionHistory[];
  historicalTableTotalPages: {
    count: number; // Intentionally done, as onChanges in child didn't detected number change, when same value, even on using cloneDeep().
  };
  // Intentionally initialized with 'true', as we don't want US specific stuffs to be shown at page load, as default will be 'undefined'.
  isSiteIntl = true;
  schedules: DistributionSchedules;
  showRatesAndYieldsSection = false;
  yields: Yields;
  latestDistributions: LatestDistribution[] = [];
  productType: ConfigurationId;
  public displayCapGainRecords: boolean;
  public hideDistributionPerShare = false;
  public hideRatesAndYieldsPerFund = false;
  constructor(
    private distributionService: DistributionService,
    private siteConfig: SiteConfigService,
    private changeDetector: ChangeDetectorRef
  ) {
    super();
  }

  populate(
    componentConfig: PricingDistributionConfig,
    brConfig: ProductDetailConfiguration,
    component: BrComponent,
    page: Page
  ): void {
    this.initialize(brConfig, component, page);
    logger.debug(page, this.page);
    this.componentConfiguration = componentConfig;
    this.displayCapGainRecords = brConfig.displayCapGainRecords;
    this.changeDetector.detectChanges();
    if (this.brConfig.fundId) {
      this.distributionService.setConfigurationName(
        componentConfig.configurationName
      );
      this.dataSubscription.push(
        this.distributionService.distributionState.subscribe(
          (newState: DistributionState) => this.mapState(newState)
        )
      );
      this.dataSubscription.push(
        this.distributionService
          .getCurrentPage$()
          .subscribe(
            (historicalList) =>
              (this.filteredHistoricalList = cloneDeep(historicalList))
          )
      );
      this.dataSubscription.push(
        this.distributionService.getNumberOfPages$().subscribe((totalPages) => {
          this.historicalTableTotalPages = cloneDeep({
            count: totalPages,
          });
        })
      );
      this.distributionService.populate(brConfig);
      this.hideDistributionPerShare = this.siteConfig.hideDistributionPerShare(
        this.brConfig.fundId
      );
      this.hideRatesAndYieldsPerFund = this.siteConfig.hideRatesAndYieldsPerFund(
        this.brConfig.fundId
      );
    }
  }

  /**
   * Called when state changes.
   * @param distributionState the new state returned by distribution service
   */
  mapState(distributionState: DistributionState): void {
    this.productType = distributionState.productType as ConfigurationId;
    this.salesChargeBreakPoints = distributionState.salesChargeBreakPoints;
    this.latestConfig = distributionState.latestConfig;
    this.tableConfig = cloneDeep(distributionState.tableConfig);
    this.isSiteIntl = distributionState.isSiteIntl;
    this.schedules = distributionState.schedules;
    this.yields = distributionState.yields;
    this.latestDistributions = distributionState.latestDistributions;

    // *** OUTDATED :: NGC-660 - LOGIC: Rates & Yields section visibility ***
    // When, distribution reported as:
    // 1. Annually / Semi-Annually: Do Not Show
    // 2. Monthly / Quarterly: Show

    // *** UPDATED :: NGC-8515 ***
    // Hide, only in case of Annually / Semi-Annually.
    // Now, PDS has string of values for the flag. They would need clean-up.
    // Like, Annually/Monthly/Annually/in October/December/Daily/Semiannually/Quarterly/-/No/Semi-annual/Semi-Annually
    // So, currently, checking only for 'annual' as it would cover "Annually/Semi-Annually".
    this.checkAnnualForDistribution(distributionState);

    this.changeDetector.detectChanges();
  }
  /**
   *
   * @param distributionState the new state returned by distribution service
   */
  private checkAnnualForDistribution(distributionState) {
    if (
      distributionState.schedules &&
      distributionState.schedules?.dividendFrequency !== '-' &&
      distributionState.schedules.dividendFrequency !== EMDASH &&
      !distributionState.schedules.dividendFrequency
        ?.toLowerCase()
        .includes('annual') &&
      (this.salesChargeBreakPoints?.length ||
        (this.yields &&
          Object.keys(this.yields).some((prop: keyof Yields) =>
            hasValue(this.yields, prop)
          )))
    ) {
      this.showRatesAndYieldsSection = true;
    }
  }
  /**
   * Call distribution service to change distribution year for table.
   * This will then update state & distribution table, when service provides update.
   */
  updateDistributionHistoryTable(selectedYear: string): void {
    this.distributionService.createDistributionTable(selectedYear);
  }

  /**
   * Trigger page change with the page number selected.
   * @param pageNumber page number selected
   */
  onPageChanged(pageNumber: number): void {
    this.distributionService.goToPage(pageNumber);
  }

  /**
   * Trigger download action.
   */
  onDownloadButtonClicked(): void {
    this.distributionService.triggerDownloadAction();
  }

  ngOnDestroy(): void {
    // clean up subscription to avoid memory leaks
    this.dataSubscription?.forEach((subscription) =>
      subscription.unsubscribe()
    );
  }
}
